/**
 * Database Query Selector
 *
 * Provides engine-specific SQL queries for database object retrieval.
 */

import { STORAGE_ENGINE_UNSUPPORTED } from '@/core/engines/azureStorageRefusal';
import type { DatabaseEngineId, UserFacingEngineId } from '@/core/engines';
import { mssqlQueries } from './mssqlQueries';
import { pgsqlQueries } from './pgsqlQueries';
import { mysqlQueries } from './mysqlQueries';
import { sqliteQueries } from './sqliteQueries';
import { oracleQueries } from './oracleQueries';
import { kustoQueries } from './kustoQueries';

/**
 * @deprecated Use DatabaseEngineId from @/core/engines instead.
 * Kept for backward compatibility.
 */
export type DatabaseEngine = UserFacingEngineId;

/**
 * Shape required by the renderer's `getQueries` consumers — does not include
 * the optional metadata-fetcher methods (`tableOptions`, `columnEnrichments`,
 * `partition`) that engines populate at varying coverage. Those live on
 * `MetadataQueries` in `electron/metadata-providers/metadataProviderRegistry.ts`
 * and are read directly via `provider.queries.<method>` in `metadataService.ts`.
 */
export type DatabaseQueries = Omit<
  typeof mssqlQueries,
  // Optional metadata-fetcher methods (see above).
  | 'tableOptions' | 'columnEnrichments' | 'partition'
  // SQL Server user-defined-type listings. Only the MSSQL tree grows a
  // `Programmability -> Types` folder, so no other engine has to declare these
  // — `getOracleObjects` already treats a missing query key as "no objects".
  | 'tableTypes' | 'aliasTypes' | 'clrTypes'
>;

/**
 * Get the query definitions for the specified database engine.
 * Note: 'mock' engine uses mssql queries.
 * @param engine Database engine type (defaults to 'mssql')
 * @returns Query definitions object
 */
export function getQueries(engine?: DatabaseEngineId | string): DatabaseQueries {
  switch (engine) {
    case 'pgsql':
      return pgsqlQueries;
    case 'mysql':
      return mysqlQueries;
    case 'sqlite':
      return sqliteQueries;
    case 'oracle':
      return oracleQueries;
    case 'kusto':
      return kustoQueries;
    case 'azurestorage':
      // No SQL path for storage (spec §9, ruling F1): never the MSSQL default.
      throw Object.assign(new Error(STORAGE_ENGINE_UNSUPPORTED.message), { code: STORAGE_ENGINE_UNSUPPORTED.code });
    case 'mssql':
    case 'mock':
    default:
      return mssqlQueries;
  }
}

export { mssqlQueries, pgsqlQueries, mysqlQueries, sqliteQueries, oracleQueries, kustoQueries };
